// src/components/ui/articleList/ArticleListCoinBtn.tsx
'use client';

import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Avatar from '@/components/ui/avatar/Avatar';
import { AVATAR_LINK } from '@/libs/api/avatar/avatar';
import Button from '../button/submit/Button';
import DoubleCoins from '../coin/DoubleCoins';
import Coin from '../coin/Coin';
import { useAppSelector } from '@/store/hook';

type ReceiveBtnProps = {
  /** 作者的 womo_id，用來導到 /user/coin-support/{womo_id} */
  womoId: string;
};

type GiveMyBtnProps = {
  articleId: string;
  coinAvatarSrc?: string;
  /** 和 OpenArticleAnchor 一樣：開啟貼文前要做的事（例如記錄來源） */
  onBeforeEnterPost?: () => void;
  /** 此篇留言送 KOL 綠籌碼是否已發完（由上層依 article.messageGetCoin 判斷） */
  soldOut?: boolean;
  /** 若內頁已算好「是否已領取」，可以用這個覆寫（列表沒傳就走 localStorage 判斷） */
  hasClaimedOverride?: boolean;
};

type GiveBtnProps = {
  articleId: string;
  coinAvatarSrc?: string;
  coinType?: string;
  /** 和 OpenArticleAnchor 一樣：開啟貼文前要做的事（例如記錄來源） */
  onBeforeEnterPost?: () => void;
  /** 此篇 chip_give 是否已發完（由上層依 article.chipGive 判斷） */
  soldOut?: boolean;
  /** 若內頁已算好「是否已領取」，可以用這個覆寫（列表沒傳就走 localStorage 判斷） */
  hasClaimedOverride?: boolean;
};

type MeInfoLike = {
  user_id?: string | number;
  id?: string | number;
};

export const ReceiveBtn = ({ womoId }: ReceiveBtnProps) => {
  const router = useRouter();

  const handleClick = () => {
    if (!womoId) return;
    router.push(`/user/${womoId}/coin-support`);
  };

  return (
    <div className="d-flex align-items-center justify-content-between bg-grey-50 rounded-5">
      <div className="ms-3 fs-sm-1 d-flex align-items-center">
        <span className="me-2">丟顆籌碼支持我吧~ </span>
      </div>
      <Button size="md" color="brand" onClick={handleClick}>
        打賞
      </Button>
    </div>
  );
};

export const GiveMyBtn = ({
  articleId,
  coinAvatarSrc,
  onBeforeEnterPost,
  soldOut,
  hasClaimedOverride,
}: GiveMyBtnProps) => {
  const router = useRouter();

  // 取登入資訊
  const meInfo = useAppSelector((state) => state.auth.meInfo) as MeInfoLike | null;
  const myUserId = meInfo?.user_id ?? meInfo?.id;
  const isLoggedIn = !!myUserId;

  // 用 localStorage 判斷是否已領取（message_get_coin）
  const [localHasClaimed, setLocalHasClaimed] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!myUserId) {
      setLocalHasClaimed(false);
      return;
    }

    const key = `msgCoinClaimed:${articleId}:${myUserId}`;
    const val = window.localStorage.getItem(key);
    setLocalHasClaimed(val === '1');
  }, [articleId, myUserId]);

  const effectiveHasClaimed =
    hasClaimedOverride !== undefined ? hasClaimedOverride : isLoggedIn && localHasClaimed;

  // 決定按鈕狀態
  type DisabledReason = 'soldout' | 'claimed' | undefined;

  const disabledReason: DisabledReason = soldOut
    ? 'soldout'
    : effectiveHasClaimed
      ? 'claimed'
      : undefined;

  const buttonDisabled = !!disabledReason;
  const buttonLabel =
    disabledReason === 'soldout' ? '已發完' : disabledReason === 'claimed' ? '已領取' : '留言';

  // const handleClick = () => {
  //   if (buttonDisabled) return;
  //   onBeforeEnterPost?.();

  //   if (typeof window !== 'undefined') {
  //     // 在網址後面加上 query string
  //     const targetPath = `/article/${articleId}?focus=1`;
  //     console.log('[GiveMyBtn] 準備跳轉至:', targetPath);
  //     router.push(targetPath);
  //   }
  // };

  const handleClick = () => {
    if (buttonDisabled) return;

    // 1. 檢查登入狀態
    if (!isLoggedIn) {
      // 即使沒登入也先存入 Session，登入後跳轉回來依然有效
      const focusData = {
        articleId,
        coinType: 'green', // GiveMyBtn 通常預設是留言得綠籌碼
        coinAvatarSrc: coinAvatarSrc || null,
        timestamp: Date.now(),
      };
      window.sessionStorage.setItem('focusReplyForArticle', JSON.stringify(focusData));

      // 導航至登入頁
      router.push('/auth/login');
      return;
    }

    // 2. 已登入流程
    onBeforeEnterPost?.();

    if (typeof window !== 'undefined') {
      const focusData = {
        articleId,
        coinType: 'green', // 確保這裡也有傳入類型
        coinAvatarSrc: coinAvatarSrc || null,
        timestamp: Date.now(),
      };

      window.sessionStorage.setItem('focusReplyForArticle', JSON.stringify(focusData));

      // 叫醒同頁面可能存在的 BottomBar
      window.dispatchEvent(new Event('session-updated'));

      router.push(`/article/${articleId}`);
    }
  };

  return (
    <div className="d-flex align-items-center justify-content-between bg-grey-50 rounded-5">
      <div className="ms-3 fs-sm-1 d-flex align-items-center">
        <span className="me-2">留言給我就送</span>
        <Coin scale={0.25} color="green">
          <Avatar src={coinAvatarSrc || AVATAR_LINK.my} />
        </Coin>
      </div>
      <Button
        size="md"
        color={buttonDisabled ? ('disabled' as any) : 'brand'}
        disabled={buttonDisabled}
        onClick={handleClick}
      >
        {buttonLabel}
      </Button>
    </div>
  );
};

export const GiveBtn = ({
  articleId,
  coinAvatarSrc,
  coinType,
  onBeforeEnterPost,
  soldOut,
  hasClaimedOverride,
}: GiveBtnProps) => {
  const router = useRouter();

  // 取登入資訊
  const meInfo = useAppSelector((state) => state.auth.meInfo) as MeInfoLike | null;
  const myUserId = meInfo?.user_id ?? meInfo?.id;
  const isLoggedIn = !!myUserId;

  // 用 localStorage 判斷是否已領取（chip_give）
  const [localHasClaimed, setLocalHasClaimed] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!myUserId) {
      setLocalHasClaimed(false);
      return;
    }

    const key = `chipGiveClaimed:${articleId}:${myUserId}`;
    const val = window.localStorage.getItem(key);
    setLocalHasClaimed(val === '1');
  }, [articleId, myUserId]);

  const effectiveHasClaimed =
    hasClaimedOverride !== undefined ? hasClaimedOverride : isLoggedIn && localHasClaimed;

  type DisabledReason = 'soldout' | 'claimed' | undefined;

  const disabledReason: DisabledReason = soldOut
    ? 'soldout'
    : effectiveHasClaimed
      ? 'claimed'
      : undefined;

  const buttonDisabled = !!disabledReason;
  const buttonLabel =
    disabledReason === 'soldout' ? '已發完' : disabledReason === 'claimed' ? '已領取' : '留言';

  const handleClick = () => {
    if (buttonDisabled) return;

    // 1. 檢查登入狀態
    if (!isLoggedIn) {
      // 如果未登入，先將 focus 資訊存入 sessionStorage
      // 這樣登入成功重新導回此頁面時，Focus 邏輯依然能被觸發
      const focusData = {
        articleId,
        coinType,
        coinAvatarSrc: coinAvatarSrc || null,
        timestamp: Date.now(),
      };
      window.sessionStorage.setItem('focusReplyForArticle', JSON.stringify(focusData));

      // 導向登入頁面
      router.push('/auth/login');
      return;
    }

    // 2. 已登入的後續邏輯
    onBeforeEnterPost?.();

    if (typeof window !== 'undefined') {
      const focusData = {
        articleId,
        coinType,
        coinAvatarSrc: coinAvatarSrc || null,
        timestamp: Date.now(),
      };

      window.sessionStorage.setItem('focusReplyForArticle', JSON.stringify(focusData));

      // ⭐ 發出事件（針對同頁面操作）
      window.dispatchEvent(new Event('session-updated'));

      // 導向文章內頁
      router.push(`/article/${articleId}`);
    }
  };

  return (
    <div className="d-flex align-items-center justify-content-between bg-grey-50 rounded-5">
      <div className="ms-3 fs-sm-1 d-flex align-items-center">
        <span className="me-2">留言給我就送</span>
        {coinType === 'womo' ? (
          <Avatar src={coinAvatarSrc || AVATAR_LINK.my} size={24} />
        ) : (
          <Coin scale={0.25} color={coinType === 'kol_pay' ? 'red' : 'green'}>
            <Avatar src={coinAvatarSrc || AVATAR_LINK.my} />
          </Coin>
        )}
      </div>
      <Button
        size="md"
        color={buttonDisabled ? ('disabled' as any) : 'brand'}
        disabled={buttonDisabled}
        onClick={handleClick}
      >
        {buttonLabel}
      </Button>
    </div>
  );
};

export const WishBtn = () => {
  return (
    <div className="d-flex align-items-center justify-content-between bg-grey-50 rounded-5">
      <div className="ms-3 fs-sm-1 d-flex align-items-center">
        <span className="me-2">我許願想獲得</span>
        <Coin scale={0.25} color="red">
          <Avatar src={AVATAR_LINK.my} />
        </Coin>
      </div>
      <Button size="md" color="brand">
        送願
      </Button>
    </div>
  );
};

export const ExchangeBtn = () => {
  return (
    <div className="d-flex align-items-center justify-content-between bg-grey-50 rounded-5">
      <div className="ms-3 fs-sm-1 d-flex align-items-center">
        <span className="me-2">我想交換獲得</span>
        <Coin scale={0.25} color="red">
          <Avatar src={AVATAR_LINK.my} />
        </Coin>
      </div>
      <Button size="md" color="brand">
        交換
      </Button>
    </div>
  );
};

export const ReunionBtn = () => {
  return (
    <div className="d-flex align-items-center justify-content-between bg-grey-50 rounded-5">
      <div className="ms-3 fs-sm-1 d-flex align-items-center">
        <span className="me-2">拿我的</span>
        <DoubleCoins scale={0.25}>
          <Avatar src={AVATAR_LINK.my} />
        </DoubleCoins>
        <span className="ms-2">報名</span>
      </div>
      <Button size="md" color="brand">
        報名
      </Button>
    </div>
  );
};

export const SecretBtn = () => {
  return (
    <div className="d-flex align-items-center justify-content-between bg-grey-50 rounded-5">
      <div className="ms-3 fs-sm-1 d-flex align-items-center">
        <span className="me-2">拿我的</span>
        <Coin scale={0.25} color="red">
          <Avatar src={AVATAR_LINK.my} />
        </Coin>
        <span className="ms-2">解鎖</span>
      </div>
      <Button size="md" color="brand">
        解鎖
      </Button>
    </div>
  );
};

export const LotteryBtn = () => {
  return (
    <div className="d-flex align-items-center justify-content-between bg-grey-50 rounded-5">
      <div className="ms-3 fs-sm-1 d-flex align-items-center">
        <span className="me-2">拿我的</span>
        <Coin scale={0.25} color="green">
          <Avatar src={AVATAR_LINK.my} />
        </Coin>
        <span className="ms-2">抽獎</span>
      </div>
      <Button size="md" color="brand">
        抽獎
      </Button>
    </div>
  );
};

export const CollectBtn = () => {
  return (
    <div className="d-flex align-items-center justify-content-between bg-grey-50 rounded-5">
      <div className="ms-3 fs-sm-1 d-flex align-items-center">
        <span className="me-2">蒐集我的</span>
        <Coin scale={0.25} color="green">
          <Avatar src={AVATAR_LINK.my} />
        </Coin>
        <span className="ms-2">換禮</span>
      </div>
      <Button size="md" color="brand">
        換禮
      </Button>
    </div>
  );
};
